import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import moment from 'moment';
import { of, Subject, Subscription } from 'rxjs';
import { MarkAsRead, MessagesHistory } from '../../models/chat.model';
import { FiltersEnum, IdentifierTypeEnum, Message } from '../../models/chat.enum';
import { PatientEngagementService } from '../../../myPrimeRx/services/patient-engagement.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { WebPubSubService } from '../../../../services/WebPubSubService';
import { constant } from '../../../../constants/app.constants';
import { environment } from '../../../../../environments/environment';
import { CommonMessageService } from '../../../../services/messageCountService';
import { NavigationService } from '../../../../services/navigationPath';
import { messages } from '../../../../constants/app.messages';

interface ResponseData {
  communicationLogs: any[];  
}

@Component({
  selector: 'app-spc-chat-history',
  templateUrl: './spc-chat-history.component.html',
  styleUrl: './spc-chat-history.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class SpcChatHistoryComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('chatHeightCal', { static: false }) chatHeightCal!: ElementRef;
  @Input() messages: any[] = [];
  @Input() enterMessage: any[] = [];
  @Output() scrollDownNewMessages = new EventEmitter<void>(); 
  @Output() previousMessagesEvent = new EventEmitter<void>();
  @Output() unreadCountChange = new EventEmitter<number>();
  @Input() filterPeriod: string = FiltersEnum.ALLHISTORY;
  @Input() patientID: any;
  @Input() npi: any;
  @Input() searchText: string = '';
  @Input() resetMessages:any;
  @Input() patientName:any;
  originalMessages: Message[] = [];
  fetchedMessages: Message[] = [];
  groupedMessages: any[] = [];
  res!: ResponseData;
  pharmacyName:any;
  pharmacyInfo:any;
  unsubscribe$: Subject<void> = new Subject();
  public messageSubscription!: Subscription;
  isnewMsgReceived:boolean = false;
  markAsReadFlag:boolean = false;
  unreadMsgCount:any;
  unreadMsgCounts:any;
  isSecurePatientComm!:boolean;
  currentPath:any;
  isHistoryPreviewOpen = false;
  historyPreviewUrl: string | null = null;
  sasTimers = new Map<string, any>();
  failedDocIds: string[] = [];
  batchTimer: any;
  showDeleteOptions = false;
  selectedMode = false;
  wasLongPress = false;
  longPressTimer: any;
  longPressDuration = 600;
  selectedCount = 0;
  selectedMessages: any[] = [];
  selectedImage: any;
  validDocIds: any;
  errorMsg: any;
  patientID_npi: any;
  skipNextEvent: boolean = false;

  constructor(
    private _patientEngagementService:PatientEngagementService,
    private _webPubSubService: WebPubSubService,
    private navigationService: NavigationService,
    private _ngZone: NgZone,
    private commonMessageService:CommonMessageService,
    private _cd: ChangeDetectorRef) { }

  ngOnInit() {
    this.isSecurePatientComm = environment.isSecurePatientComm;
    this.navigationService.currentPath$.subscribe(path => {
      this.currentPath = path;
    });
    
    this.pharmacyInfo = JSON.parse(localStorage.getItem('PharmacyInfo') as string);
    if(this.pharmacyInfo){
      this.pharmacyName = this.pharmacyInfo?.name;
    }
    this.messageSubscription = this._webPubSubService.newMessage$.subscribe((res) => {
      if(res) {
        if (res?.Sender?.Identifier === this.npi) {
          this.isnewMsgReceived = true;
          this._ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              this.scrollToBottom();
              this.markAsReadFlag = true;
            });
          });
          this.loadMessages();
        }
      }
    });
    this.originalMessages = [...this.messages];
    this.filterMessages();
    this.adjustHeight(this.chatHeightCal);
    window.addEventListener('resize', () => this.adjustHeight(this.chatHeightCal));
    if (this.isValidPatientAndNpi()) { 
      this.loadMessages();
    }
  }

  ngAfterViewInit() {
    this.adjustHeight(this.chatHeightCal);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) { 
    this.adjustHeight(this.chatHeightCal);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchText']) {
      this.filterMessages();
    }
    if (changes['enterMessage'] && changes['enterMessage'].currentValue) {
      const incomingMessages = changes['enterMessage'].currentValue;
      if (incomingMessages.length > 0) {
        this.messages = [...this.messages, ...incomingMessages];
     const uniqueMessages = Array.from(
  new Map(
    this.messages.map(msg => {
      const id = this.extractDocId(msg.message);
      const key = id || msg.date;   // fallback key for TEXT messages
      return [key, msg];
    })
  ).values()
);
       
        this.messages = uniqueMessages;
        this.messages.forEach(msg => {
          if (msg?.sender?.userName) {
            msg.sender.userName = this.capitalizeName(msg.sender.userName);
          }
        });  
        this.sortMessages();
        this.groupMessagesByDate();
        this._ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            this.scrollToBottom();
          });
        });
      }
    }
    if (changes['filterPeriod'] && !changes['filterPeriod'].isFirstChange()) { 
      this.filterMessages();
    }
  }

  groupMessagesByDate(): void {
    const groupedMessages: any[] = [];
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const startOfWeek = moment().startOf('week');

    this.messages.forEach(msg => {
      const msgDate = moment(msg.date); 
      if (!msgDate.isValid()) return; 
      const msgDay = msgDate.startOf('day');
      let header = '';
      if (msgDay.isSame(today)) {
        header = messages.Today;
      } else if (msgDay.isSame(yesterday)) {
        header = messages.Yesterday;
      } else if (msgDay.isBetween(startOfWeek, today, undefined, '[)')) {
        header = msgDay.format('dddd'); 
      } else {
        header = msgDay.format('dddd, MMMM D'); 
      }
      if (!groupedMessages.some(g => g.isHeader && g.date === header)) {
        groupedMessages.push({ isHeader: true, date: header });
      } 
      groupedMessages.push(msg);
    });

    this.groupedMessages = groupedMessages;
  }

  adjustHeight(element: ElementRef) {
    if (element) {
      const viewportHeight = window.innerHeight; 
      const headerHeight = 150; 
      const footerHeight = 50;  
      const availableHeight = viewportHeight - headerHeight - footerHeight; 
      element.nativeElement.style.height = `${availableHeight}px`; 
      const htmlDivs = element.nativeElement.querySelectorAll('.spc-history');
      let totalDivsHeight = 0;
      htmlDivs.forEach((item: HTMLElement) => {
        totalDivsHeight += item.offsetHeight;
      }); 
      if (totalDivsHeight <= availableHeight) {
        element.nativeElement.style.height = 'auto';
        document.body.style.overflow = 'hidden';
      } else {
        element.nativeElement.style.height = `${availableHeight}px`;
      }
    }
  }

  filterMessages(): void {
    const searchLower = this.searchText.toLowerCase();
    const now = moment(); 
    this.messages = this.originalMessages.filter(m => {
      const messageDate = moment(m.date);
      const matchesSearch = m.message.toLowerCase().includes(searchLower);
      let matchesDate = true;
      if (this.filterPeriod) {
        switch (this.filterPeriod) {
          case FiltersEnum.TODAY:
            matchesDate = messageDate.isSame(moment(now), 'day');
            break;
          case FiltersEnum.LASTONEWEEK:
            matchesDate = messageDate.isAfter(moment(now).subtract(1, 'weeks'));
            break;
          case FiltersEnum.LASTONEMONTH:
            matchesDate = messageDate.isAfter(moment(now).subtract(1, 'months'));
            break;
          case FiltersEnum.LASTSIXMONTHS:
            matchesDate = messageDate.isAfter(moment(now).subtract(6, 'months'));
            break;
          case FiltersEnum.LASTONEYEAR:
            matchesDate = messageDate.isAfter(moment(now).subtract(1, 'years'));
            break;
          default:
            matchesDate = true;
            break;
        }
      } 
      return matchesSearch && matchesDate;
    });

    this.messages.forEach(msg => {
      if (msg?.sender?.userName) {
        msg.sender.userName = this.capitalizeName(msg.sender.userName);
      }
    });

    this.sortMessages();
    this.groupMessagesByDate();
    this._ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.scrollToBottom();
      });
    });
  }

  private isValidPatientAndNpi(): boolean {
    return !!this.patientID && !!this.npi;
  }

  onScroll(event: any) {
    const element = event.target;
    if (element.scrollTop === 0) { 
      if (this.isValidPatientAndNpi()) {
        //this.loadMessages();
      }
    } else if (element.scrollHeight - element.scrollTop === element.clientHeight) {
      this.loadMoreMessages(); 
    }
  }

  loadMoreMessages() {}

  parseMessage(message: string): string {
  const visiblePart = message?.split('~&~')[0]?.trim() || '';
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return visiblePart.replace(urlRegex, '<label>$1</label>');
}

  parseMessageForPatient(message: string): string {
    const visible = (message || '').split('~&~')[0] || '';
    return visible.replace(/https?:\/\/\S+/g, '').trim();
  }

  loadMessages() {
    const startDateTime = this.getStartDateByFilter();
    const endDateTime = moment().format('YYYY-MM-DD') || null;
    const payLoad: MessagesHistory = {
      sender: {
        Identifier: ""+this.patientID,
        IdentifierType: IdentifierTypeEnum.PATIENT
      },
      recipient: {
        Identifier: this.npi,
        IdentifierType: IdentifierTypeEnum.NPI
      },
      StartDateTime: startDateTime,
      EndDateTime: endDateTime,
      PageIndex: constant.CHAT_PAGE_MIN,
      PageSize: constant.CHAT_PAGE_MAX,
      pharmacyNPI: this.npi,
      SenderType: 0
    };
    this._patientEngagementService.postConversation(payLoad)
    .pipe(
      takeUntil(this.unsubscribe$),
      catchError((error) => {
        return of({ communicationLogs: [] });
      })
    )
    .subscribe(async (res) => { 
      if (res && res?.communicationLogs) {
        let apiMessages = res.communicationLogs;
        this.messages = [...apiMessages, ...this.messages];
        // this.messages = [...apiMessages];
        this.removeDuplicateMessages();
        this.messages.forEach(msg => {
          if (msg?.sender?.userName) {
            msg.sender.userName = this.capitalizeName(msg.sender.userName);
          }
        });
      const uniqueMessages = Array.from(
  new Map(
    this.messages.map(msg => {
      const id = this.extractDocId(msg.message);
      const key = id || msg.date;   // fallback key for TEXT messages
      return [key, msg];
    })
  ).values()
);
    
        this.messages = uniqueMessages;
        this.originalMessages = [...this.messages];
         await this.refreshImageUrls();
        if (this.messages && this.messages?.length > 0) {
          this.sortMessages();
          this.groupMessagesByDate();
          this._ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              this.scrollToBottom();
              this.markAsReadFlag = true;
              if(this.currentPath === 'spc'){ 
                this.markAsRead();
              }  
            });
          });
        }
      }
    });
  }

  sortMessages(): void {
    this.messages = this.messages.sort((a, b) => moment(a.date, 'YYYY-MM-DD hh:mm:ss A').diff(moment(b.date, 'YYYY-MM-DD hh:mm:ss A')));
  }

  private capitalizeName(name: string): string {
    return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  markAsRead(){
    if(this.markAsReadFlag && this.npi && this.patientID){
      const usersPayLoad: MarkAsRead = {
        Sender: {
          Identifier: this.npi,
          IdentifierType: IdentifierTypeEnum.NPI 
        },
        Recipient: {
          Identifier: ""+this.patientID,
          IdentifierType: IdentifierTypeEnum.PATIENT
        }
      };
      this._patientEngagementService.postMessageMarkAsRead(usersPayLoad)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any['StatusCode']) => {
        if (res) {
          if(res?.StatusCode === 0){
            this.markAsReadFlag = false;
            this.commonMessageService.getCounts();
          }
        }
      });
    }
  }

  scrollToBottom() {
    const chatHistory = document.querySelector('.spc-history');
    if (chatHistory) {
      chatHistory.scrollTop = chatHistory.scrollHeight;
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getUrls(msg?: string): string[] {
  if (!msg) return [];
  const visible = (msg.split('~&~')[0] || '').trim();
  const urls = visible.match(/https?:\/\/\S+/g) || [];
  return urls;
}

onImgErr(ev: Event, oldUrl: string) {
  const msg = this.messages.find(m => (m?.message || '').includes(oldUrl));
  if (!msg) return;
  const [visible, hidden = ""] = msg.message.split("~&~");
  if (!hidden.startsWith("img:")) return;

  const docIds = hidden.replace("img:", "").split(",").map((x: any) => x.trim()).filter(Boolean);
  if (!docIds.length) return;

  const urls = visible.match(/https?:\/\/\S+/g) || [];
  const failedIndex = urls.indexOf(oldUrl);
  if (failedIndex === -1) return;

  const failedDocId = docIds[failedIndex];
  this.failedDocIds.push(failedDocId);

  clearTimeout(this.batchTimer);
  this.batchTimer = setTimeout(async () => {
    const uniqueIds = [...new Set(this.failedDocIds)];
    this.failedDocIds = [];

    const map = await this.downloadImagesByIds(uniqueIds);

    this.messages.forEach(m => {
      const [v, h = ""] = (m.message || "").split("~&~");
      if (!h.startsWith("img:")) return;

      const mDocIds = h.replace("img:", "").split(",").map((x: any) => x.trim()).filter(Boolean); // Document IDs in this message
      const mUrls = v.match(/https?:\/\/\S+/g) || []; // URLs in the visible part of the message
      let updatedVisible = v;
      mDocIds.forEach((id: any, idx: number) => {
        const fresh = map[id];
        if (fresh && mUrls[idx]) {
          updatedVisible = updatedVisible.replace(mUrls[idx], fresh);
          this.armSasAutoRefresh(fresh, m);
        }
      });
      m.message = updatedVisible + "~&~" + h;
    });
    this._cd.detectChanges();
  }, 150);
}



  openHistoryPreview(url: string) {
    this.historyPreviewUrl = url;
    this.isHistoryPreviewOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeHistoryPreview() {
    this.isHistoryPreviewOpen = false;
    this.historyPreviewUrl = null;
    document.body.style.overflow = '';
  }

  async downloadImagesByIds(docIds: string[]): Promise<Record<string, string>> {
    const payload = {
      Pharmacy: { IdentifierType: 0, Identifier: this.npi, Name: this.pharmacyName || "TEST Community Pharmacy" },
      Application: { Id: 1, Name: "Primerx" },
      DocumentIds: docIds,
      RelatedEntity: { EntityType: 0, Identifier: String(this.patientID), Name: this.patientName || "Patient" },
      DocumentReference: 2,
      SenderType: 0
    };
    try {
      const resp: any = await this._patientEngagementService.downloadImage(payload).toPromise();
      const documentUrlMap: Record<string, string> = {};
      if (Array.isArray(resp?.documents)) {
        for (const d of resp.documents) {
          if (d?.document?.id && d?.document?.url) {
            documentUrlMap[d.document.id] = d.document.url;
          }
        }
      }
      return documentUrlMap;
    } catch (e) {
      return {};
    }
  }

async refreshImageUrls() {
  // 1. Collect all docIds
  const allDocIds: string[] = [];
  for (const msg of this.messages) {
    const [, hidden = ""] = (msg?.message || "").split("~&~");
    if (!hidden.startsWith("img:")) continue;

    const docIds = hidden.replace("img:", "").split(",").map((x: any) => x.trim()).filter(Boolean);

    allDocIds.push(...docIds);
  }
  if (allDocIds.length === 0) return;

  // Unique valid docIds
  this.validDocIds = [...new Set(allDocIds)];

  // 2. Download fresh URLs for ONLY active images
  const idToUrlMap = await this.downloadImagesByIds(this.validDocIds);

  // 3. STRICT — Rebuild visible URLs ONLY from idToUrlMap (no fallback)
  for (const msg of this.messages) {
    const [visiblePart = "", hidden = ""] = (msg.message || "").split("~&~");

    if (!hidden.startsWith("img:")) continue;

    const docIds = hidden.replace("img:", "").split(",").map((x: any) => x.trim()).filter(Boolean);

    // Build new visible section using only fresh mapped URLs
    const freshUrls = docIds
      .map((id: any) => idToUrlMap[id])  // if undefined = ignored
      .filter((url: any) => !!url);      // remove undefined

    //old URLs are not reused
    const newVisible = freshUrls.join("\n");

    // Combine updated sections
    msg.message = newVisible + "~&~" + hidden;
  }

  this._cd.detectChanges();
}



  armSasAutoRefresh(url: string, msg: any, originalUrl?: string) {
    const exp = this.getSasExpiry(url);
    if (!exp) return;
    const refreshAt = exp - 2 * 60 * 1000;
    const now = Date.now();
    const delay = Math.max(5_000, refreshAt - now);
    if (this.sasTimers.has(url)) return;
    const t = setTimeout(async () => {
      this.sasTimers.delete(url);
      const hidden = (msg.message.split('~&~')[1] || '');
      if (!hidden.startsWith('img:')) return;
      const docIds = hidden.slice(4).split(',').filter(Boolean);
      if (!docIds.length) return;
      const urls = await this.downloadImagesByIds(docIds);
      if (!urls.length) return;
      const fresh = urls[0];
      msg.message = msg.message.replace(url, fresh);
      this._cd.detectChanges();
      this.armSasAutoRefresh(fresh, msg);
    }, delay);
    this.sasTimers.set(url, t);
  }

  getSasExpiry(url: string): number | null {
    try {
      const u = new URL(url);
      const se = u.searchParams.get('se');
      if (!se) return null;
      return Date.parse(se);
    } catch {
      return null;
    }
  }

  removeDuplicateMessages() {
    const map = new Map();
    this.messages.forEach(m => {
      const hidden = (m.message?.split('~&~')[1] || '').trim();
     const docId = this.extractDocId(m.message);
      const key = docId || `${m.sender.identifier}-${m.date}`;
      map.set(key, m);
      map.set(key, m);
    });
    this.messages = Array.from(map.values());
  }
   private getStartDateByFilter(): string {
  const now = moment();

  switch (this.filterPeriod) {
    case FiltersEnum.TODAY:
      return now.startOf('day').format('YYYY-MM-DD');
    case FiltersEnum.LASTONEWEEK:
      return now.subtract(1, 'weeks').format('YYYY-MM-DD');
    case FiltersEnum.LASTONEMONTH:
      return now.subtract(1, 'months').format('YYYY-MM-DD');
    case FiltersEnum.LASTSIXMONTHS:
      return now.subtract(6, 'months').format('YYYY-MM-DD');
    case FiltersEnum.LASTONEYEAR:
      return now.subtract(1, 'years').format('YYYY-MM-DD');
    default:
      return moment('1900-01-01').format('YYYY-MM-DD');
  }
}


onPressStart(message: any, event: Event) {
  event.stopPropagation();
  this.wasLongPress = false;
  this.selectedImage = message;
  this.longPressTimer = setTimeout(() => {
    this.wasLongPress = true;
    if (this.isIOS()) {
      this.skipNextEvent = true;
    }
    (event.target as HTMLElement).classList.add('block-click-once');
    this.selectedMode = true;
    message.isSelected = true;
    this._cd.detectChanges();
    this.collectSelectedMessages();
  }, this.longPressDuration);
}

onPressEnd(event?: Event) {
    if (this.isIOS() && this.skipNextEvent) {
      if (event) {
    event.preventDefault();
    event.stopPropagation();
      }

    this.skipNextEvent = false;
    return;
  }
  if (!this.wasLongPress) {
    clearTimeout(this.longPressTimer);
  }
}

onImageClick(url: string, message: any, event: Event) {
  const el = event.target as HTMLElement;
  if (el.classList.contains('block-click-once')) {
    el.classList.remove('block-click-once');
    return;
  }
    if (this.isIOS() && this.skipNextEvent) {
    this.skipNextEvent = false; 
    return;
  }
  if (this.selectedMode) {
    message.isSelected = !message.isSelected;
    this.collectSelectedMessages();
    event.stopPropagation();
    return;
  }
  this.openHistoryPreview(url);
}

collectSelectedMessages() {
  this.selectedMessages = this.groupedMessages.filter(m => m.isSelected);
  this.selectedCount = this.selectedMessages.length;
  if (this.selectedCount === 0) {
    this.selectedMode = false;
  }
}
openDeleteOptions() {
  this.showDeleteOptions = true;
}

closeDeleteOptions() {
  this.showDeleteOptions = false;
}
deleteForMe() {
  this.closeDeleteOptions();
}

deleteForEveryone() {
  this.closeDeleteOptions();
}
clearSelection() {
  this.groupedMessages.forEach(m => m.isSelected = false);
  this.selectedMessages = [];
  this.selectedMode = false;
  this.selectedCount = 0;
}
deleteImage(forMe: boolean) {
const docIds: string[] = [];
(this.selectedMessages || []).forEach(msg => {
  const hidden = (msg?.message || '').split('~&~')[1] || '';

  if (hidden.startsWith('img:')) {
    const extracted = hidden.replace('img:', '').split(',')[0].trim();
    if (extracted) {
      docIds.push(extracted);
    }
  }
});


  if (!docIds.length) return;
  const payload = {
    Pharmacy: { IdentifierType: 0, Identifier: this.npi, Name: this.pharmacyName },
    Application: { Id: 1, Name: "primerx" },
    DocumentIDs: docIds,
    DeleteType: forMe ? 0 : 1,
    SenderType: 0,
  };

  this._patientEngagementService.deleteImage(payload).subscribe( async resp => {
    if (resp?.statusCode === 0) {
  this.selectedMessages.forEach(msg => {
    const hidden = msg.message.split("~&~")[1] || "";

    if (hidden.startsWith("img:")) {
      const docIds = hidden.replace("img:", "").split(",").map((x: any) => x.trim());
      docIds.forEach((id: any) => {
        if (docIds.includes(id)) {
          this.removeImageFromMessage(msg, id);
        }
      });
    }
  });
 this.showDeleteOptions = false;
    this.selectedMode = false;
     this.selectedCount = 0;
    this.selectedMessages = [];
    this.selectedImage = null;
    this.groupedMessages.forEach(m => m.isSelected = false);
   
  this.addDeletedMessageBubble(forMe);
  await this.loadMessages();
  this._cd.detectChanges();
}
  });
}

hideFailedImage(message: any, url: string) {
  if (!message.failedImages) message.failedImages = new Set<string>();
  message.failedImages.add(url);
  this._cd.detectChanges();
  
}
 async addDeletedMessageBubble(forMe: boolean) {
  const finalText = `
    <div class="spc-delete-img">
      <img src="./assets/images/ban-solid-full.svg" class="error-icon" />
      <i>${forMe ? 'You deleted this image for you' : 'You deleted this image for everyone'}</i>
    </div>
  `; 
this.patientID_npi = `${this.patientID}_${this.npi}`;
  const enterMessage = {
    date: moment().toISOString(),
    message: finalText,
    sender: { identifier: this.patientID, identifierType: IdentifierTypeEnum.PATIENT, userName: this.patientName },
    lastMessageReceivedAt: new Date().toISOString(),
    isRead: false
  };
  try {
    await this._webPubSubService.sendMessageToPharmacist(this.patientID_npi, finalText, this.patientName);
    this.enterMessage = [enterMessage];
    this._cd.detectChanges();  
  this._cd.detectChanges();
}
  catch (error) {
    console.error('Error sending deleted message:', error);
  } 
  }
extractDocId(msg: string): string | null {
  const parts = msg?.split("~&~img:");
  if (!parts[1]) return null;
  return parts[1].split(",")[0].trim();
}
isDeletedMessages(message: string): boolean {
  if (!message) return false;
  return message.includes('spc-delete-img');
}
removeImageFromMessage(msg: any, deletedDocId: string) {
  const [visible, hidden = ""] = msg.message.split("~&~");

  const docIds = hidden.replace("img:", "")
    .split(",")
    .map((x: any) => x.trim())
    .filter(Boolean);
  const remainingDocIds = docIds.filter((id: any) => id !== deletedDocId);
  const urls = visible.match(/https?:\/\/\S+/g) || [];
  const remainingUrls = urls.filter((u: any, index: any) => docIds[index] !== deletedDocId);

  const newVisible = remainingUrls.join("\n");

  msg.message = newVisible + "~&~img:" + remainingDocIds.join(",");

  return msg;
}
isIOS(): boolean {
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  return (
    /iPad|iPhone|iPod/.test(platform)
    || (platform === 'MacIntel' && maxTouchPoints > 1)
  );
}

}
